import workspace from "../workspace";
import {v4 as uuidv4} from "uuid";
import store from "../../../../../../store";
import {addResource, changeResource} from "../../../../../../actions/sqftResourceFunctions";
import {getEdgeFeatures} from "../../../../helpers/shapeHelper";
import {getBacksplashArea, getEdgeArea, getEdgeLength} from "../../../../helpers/sqftHelper";
import {RESOURCE_ATTACHED_TO_TYPE, RESOURCE_TYPE} from "../../../../constants";
import {setSQFTPopup} from "../../../../../../actions/sqftMenuFunctions";
import {getQtCatName} from "../../../../helpers/sqftQuotesHelper";
import sqftConfig from "../../store/sqftConfig";
import {getWizardGroupsFromTheWizard} from "../../../../helpers/sqftHelperUi";
import {getCurrentLayout} from "../../../../../appLayout";

/**
 * Default configuration for attachResource
 */
const DEFAULT_ATTACH_CONFIG = {
  height: 0,
  resourceId: null,
  qtCatName: null,
  addResourceToStore: true,
  shouldShowEyeBall: true,
  fromWizardGroupId: null,
};

/**
 * Legacy wrapper for backward compatibility
 * Converts old function signature to new config-based approach
 * @deprecated Use attachResourceWithConfig instead
 */
export const attachResource = (
  seg,
  segCorner,
  resourceType,
  featureId,
  partId,
  featureName,
  height = 0,
  resourceId = null,
  qtCatName = null,
  addResourceToStore = true,
  shouldShowEyeBall = true,
) => {
  // Convert legacy parameters to config object
  const config = {
    seg,
    segCorner,
    resourceType,
    featureId,
    partId,
    featureName,
    height,
    resourceId,
    qtCatName,
    addResourceToStore,
    shouldShowEyeBall,
  };

  return attachResourceWithConfig(config);
};

/**
 * Modern config-based approach for attaching resources
 * @param {Object} config - Configuration object
 * @param {*} config.seg - Line segment
 * @param {*} config.segCorner - Corner segment
 * @param {*} config.resourceType - Type of resource to attach
 * @param {number} config.featureId - Feature identifier
 * @param {string} config.partId - Part identifier
 * @param {*} config.featureName - Name of the feature
 * @param {number} [config.height=0] - Height parameter
 * @param {string} [config.resourceId=null] - Resource identifier
 * @param {*} [config.qtCatName=null] - Quote category name
 * @param {boolean} [config.addResourceToStore=true] - Whether to add resource to store
 * @param {boolean} [config.shouldShowEyeBall=true] - Whether to show eye ball indicator
 * @param {string} [config.fromWizardGroupId=null] - New wizard group identifier for export
 */
export const attachResourceWithConfig = (config) => {
  // Merge with defaults to ensure all properties exist
  const fullConfig = { ...DEFAULT_ATTACH_CONFIG, ...config };

  const {
    seg,
    segCorner,
    resourceType,
    featureId,
    partId,
    featureName,
    height,
    resourceId,
    qtCatName,
    addResourceToStore,
    shouldShowEyeBall,
    fromWizardGroupId,
    fromWizardId,
    fromWizardStep,
  } = fullConfig;

  const attachedToHovered = segCorner || seg;
  const attachedToExisting = workspace.getAttachedTo(attachedToHovered);

  const attachedToResource = prepareResource(
    seg,
    segCorner,
    resourceType,
    featureId,
    partId,
    featureName,
    resourceId,
    qtCatName,
    height,
    shouldShowEyeBall,
    fromWizardGroupId,
    fromWizardId,
    fromWizardStep,
  );

  attachResourceToLine(seg, segCorner, resourceType, featureId, partId, featureName, height, resourceId);

  if (addResourceToStore) {
    createAttachedResourceInStore(attachedToExisting, attachedToResource);
  }

  closeSQFTPopup();
};

const createAttachedResourceInStore = (shouldChangeExisting, attachedToResource) => {
  const action = shouldChangeExisting ? changeResource : addResource;
  store.dispatch(action(RESOURCE_TYPE.attachedToLine, attachedToResource));
};

const attachResourceToLine = (seg, segCorner, resourceType, featureId, partId, featureName, height, resourceId) => {
  let historyCallBack = () => {};
  let newSection;

  const resourceActionMap = {
    'edge': (segCorner) ? segCorner.addEdgeToCornerLine.bind(this, featureName, featureId) : seg.addEdgeToLine.bind(this, featureName, featureId),
    'backsplash': (segCorner) ? segCorner.addBacksplashToCornerLine.bind(this, featureName, resourceType, featureId, height) : seg.addBacksplashToLine.bind(this, featureName, resourceType, featureId, height),
    'wall': (segCorner) ? segCorner.addBacksplashToCornerLine.bind(this, featureName, resourceType, featureId, height) : seg.addBacksplashToLine.bind(this, featureName, resourceType, featureId, height),
    [RESOURCE_ATTACHED_TO_TYPE.bumpout]: () => {
      if (seg && !seg.isWithBumpout()) seg.addBumpoutToLine(resourceId);
    },
    [RESOURCE_ATTACHED_TO_TYPE.mitered]: () => {
      if (seg && !seg.isWithMitered()) {
        newSection = seg.addMiteredToLine();
        if (newSection) workspace.miteredController.addSection(newSection);
      }
      if (segCorner && !segCorner.isWithMitered()) {
        newSection = segCorner.addMiteredToCornerLine();
        if (newSection) workspace.miteredController.addSection(newSection);
      }
    },
    [RESOURCE_ATTACHED_TO_TYPE.underlayment]: () => {
      if (seg && !seg.isWithMitered()) {
        newSection = seg.addUnderlaymentToLine();
        if (newSection) workspace.underlaymentController.addSection(newSection);
      }
    },
    [RESOURCE_ATTACHED_TO_TYPE.crown_molding]: () => seg.addCrownMoldingToLine(featureName, featureId),
    [RESOURCE_ATTACHED_TO_TYPE.end_cap]: () => seg.addEndCapToLine(featureName, featureId),
  };

  if (resourceActionMap[resourceType]) {
    historyCallBack = resourceActionMap[resourceType];
  }

  historyCallBack();
};

export const closeSQFTPopup = () => {
  const state = store.getState();
  const isSQFTPopupOpened = state.sqftMenuState.sqftPopup.isOpened;

  if (isSQFTPopupOpened) {
    store.dispatch(setSQFTPopup({
      title: workspace.intl.formatMessage({ id: "sqft.popup.title.systemMessage" }),
      content: '',
      isOpened: false,
      confirmCallback: () => {},
      confirmText: '',
      cancelCallback: () => {},
      cancelText: ''
    }));
  }
};

const prepareResource = (
  seg,
  segCorner,
  resourceType,
  featureId,
  partId,
  featureName,
  resourceId,
  qtCatName,
  height,
  shouldShowEyeBall = true,
  fromWizardGroupIdParam = null,
  fromWizardIdParam= null,
  fromWizardStepParam= null,
) => {
  const attachedToHovered = segCorner || seg;
  const id = resourceId || (attachedToHovered && attachedToHovered.id) || uuidv4();

  let attachedToParentId = null;
  if (attachedToHovered && attachedToHovered.getParent) {
    const parent = attachedToHovered.getParent();
    attachedToParentId = parent ? parent.id : null;
  }
  let {fromWizardId, fromWizardStep, fromWizardGroupId} = getWizardPropertiesForFeatureId(featureId);
  const layout = getCurrentLayout();
  const feature = layout.getFeatureById(featureId, true);

  const resource = {
    id,
    type: resourceType,
    featureId,
    name: featureName,
    backsplashFeatureName: feature?.getFeatureName(),
    attachedToId: attachedToHovered ? attachedToHovered.id : null,
    partId,
    part: segCorner ? 'lineCorner' : 'line',
    attachedToParentId,
    isDisabledInPopup: attachedToHovered ? attachedToHovered.isDisabledInPopup : null,
    qtCatName: qtCatName || getQtCatName(),
    shouldShowEyeBall,
    height,
    fromWizardId: fromWizardIdParam || fromWizardId,
    fromWizardStep: fromWizardStepParam || fromWizardStep,
    fromWizardGroupId: fromWizardGroupIdParam || fromWizardGroupId,
  };

  // Add export method
  resource.export = function() {
    let length = 0, realLength = 0, lineLength = 0, area = 0;
    const line = workspace.getLineOrCornerLineById(this.attachedToId);

    if (line) {
      switch (resourceType) {
        case RESOURCE_ATTACHED_TO_TYPE.edge: {
          const navItems = getEdgeFeatures();
          const navItem = navItems.find(edge => edge.part.dbFeature.id === this.featureId);
          const feature = navItem ? navItem.part : null;
          const unit = feature ? feature.dbFeature.unit : null;
          length = (unit && unit.toLowerCase() === "each") ? 1 : getEdgeLength(line);
          realLength = getEdgeLength(line);
          lineLength = sqftConfig.measureUnit === "Imperial" ? line.getLineLength() : line.getLineLengthMm();
          area = getEdgeArea(line, lineLength, featureId);
          break;
        }
        case RESOURCE_ATTACHED_TO_TYPE.backsplash:
          length = getBacksplashArea(line.getBacksplashHeight(), line);
          break;
        default:
          length = realLength = getEdgeLength(line);
          break;
      }

      const exportData = { ...this, quantity: length, length, realLength, lineLength, area };

      return exportData;
    }

    return {};
  };

  return resource;
};

export const getWizardPropertiesForFeatureId = (featureId) => {
  const layout = getCurrentLayout();
  const feature = layout.getFeatureById(featureId, true);

  return getWizardPropertiesForFeature(feature);
}

export const getWizardPropertiesForFeature = (feature, includeHidePriceNoCart = false) => {
  const {wizardState} = store.getState();
  const layout = getCurrentLayout();
  const wizardIsActive = wizardState.wizardIsActive;

  if (wizardIsActive && feature) {
    const featureGroup = layout.getFeatureGroupByPartId(feature.partId);
    if (featureGroup) {
      const wizardSteps = getWizardGroupsFromTheWizard(
        featureGroup.dbName || featureGroup.partName,
        null,
        feature,
        null,
        null,
        false,
        includeHidePriceNoCart
      );
      if (wizardSteps.length) {
        const fromWizardId = wizardState.wizardActiveItem.id;
        const fromWizardStep = wizardSteps[0].stepIndex;
        const fromWizardGroupId = wizardSteps[0].stepGroups[0].id;
        return {fromWizardId, fromWizardStep, fromWizardGroupId}
      }
    }
  }

  return {fromWizardId: null, fromWizardStep: null, fromWizardGroupId: null}
}
